import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { useQueryState } from "nuqs";
import { useMessageApi } from "../../../provider/AntdNotification";
import { getSingleImageUrl } from "../../../lib/utils";
import TabsContain from "../../../views/components/reuseComp/TabsContain";
import {
  useHomePageDetail,
  useHomePageSave,
} from "../../../hooks/api/homepage/hooks";
import HomeBanner from "./HomeBanner";
import WhyUsSection from "./whyUsSection";
import WhatSetsUsApart from "./whatSetsUsApart";
import OurServicesSection from "./OurServicesSection";
import IndustriesWeServe from "./industriesWeServe";
import TrackRecord from "./trackRecord";
import GetConnected from "./getConnected";
import Footer from "./Footer";
import { IHomePagePayload } from "../../../types/api/homepage";
import OurThinking from "./ourThinking";
import { useServiceList } from "../../../hooks/api/service/hooks";
import { ServiceItem } from "../../../types/api/service";
import GlobalReach from "./globalReach";
import { useIndustryList } from "../../../hooks/api/industry/hook"
import { IndustryItem } from "../../../types/api/industry";

export type SectionField =
  | "industriesWeServe"
  | "trackRecord"
  | "breadcrumb"
  | "whatWeStandFor"
  | "globalReach";

const HomeTabs: React.FC<{ setBreadcrumb: (value: string) => void }> = ({
  setBreadcrumb,
}) => {
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const [form] = Form.useForm();

  const activeTab = tab || "Breadcrumb";
  const [isEditing, setIsEditing] = useState(false);
  const [pageData, setPageData] = useState<IHomePagePayload | null>(null);

  const { data } = useHomePageDetail();
  const { mutate: updateHomeData, isPending } = useHomePageSave({});

  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (data?.data) {
      setPageData(data.data);
    }
  }, [data]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setBreadcrumb(section);
    setIsEditing(false);
    setReviewMode("list");
    setEditingReviewIndex(null);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!pageData?._id) return;

      let payload: IHomePagePayload = { ...pageData };

      if (values.whyUs) {
        payload.whyUs = {
          ...pageData.whyUs,
          ...values.whyUs,
          image: getSingleImageUrl(values.whyUs.image),
        };
      }

      if (values.whatSetsUsApart) {
        payload.whatSetsUsApart = {
          heading:
            values.whatSetsUsApart.heading ?? pageData.whatSetsUsApart?.heading ?? "",
          subtext:
            values.whatSetsUsApart.subtext ?? pageData.whatSetsUsApart?.subtext ?? "",
          text:
            values.whatSetsUsApart.text ?? pageData.whatSetsUsApart?.text ?? [],
        };
      }

      if (values.ourServices) {
        payload.ourServices = {
          ...pageData.ourServices,
          ...values.ourServices,
          selectedServices:
            values.ourServices.selectedServices ??
            pageData.ourServices?.selectedServices ??
            [],
        };
      }

      if (values.industriesWeServe) {
        payload.industriesWeServe = {
          ...pageData.industriesWeServe,
          ...values.industriesWeServe,
          selectedIndustry:
            values.industriesWeServe.selectedIndustry ??
            pageData.industriesWeServe?.selectedIndustry ??
            [],
        };
      }

      if (activeTab === "Global Reach") {
        const updated = [...(pageData.globalReach?.locationPin || [])];
        const item = values.globalReach?.locationPin?.[0];

        if (item) {
          const data = {
            order: Number(item.order) || 0,
            latitude: Number(item.latitude) || 0,
            longitude: Number(item.longitude) || 0,
          };

          if (reviewMode === "add") updated.push(data);
          if (reviewMode === "edit" && editingReviewIndex !== null) {
            updated[editingReviewIndex] = data;
          }
        }

        payload.globalReach = {
          heading: values.globalReach?.heading ?? pageData.globalReach?.heading ?? "",
          subtext: values.globalReach?.subtext ?? pageData.globalReach?.subtext ?? "",
          locationPin: updated,
        };
      }

      if (activeTab === "Track Record") {
        const updated = [...(pageData.trackRecord?.track || [])];
        const item = values.trackRecord?.track?.[0];

        if (item) {
          const data = {
            label: item.label,
            digit: Number(item.digit) || 0,
            suffix: item.suffix,
          };

          if (reviewMode === "add") updated.push(data);
          if (reviewMode === "edit" && editingReviewIndex !== null) {
            updated[editingReviewIndex] = data;
          }
        }

        payload.trackRecord = {
          heading: values.trackRecord?.heading ?? pageData.trackRecord?.heading ?? "",
          subtext: values.trackRecord?.subtext ?? pageData.trackRecord?.subtext ?? "",
          track: updated,
        };
      }

      if (activeTab === "Our Thinking") {
        payload.ourThinking = {
          ...pageData.ourThinking,
          ...values.ourThinking,
          selectedArticle:
            values.ourThinking?.selectedArticle ??
            pageData.ourThinking?.selectedArticle ??
            [],
        };
      }

      if (values.getConnected) {
        payload.getConnected = {
          ...pageData.getConnected,
          ...values.getConnected,
          image: getSingleImageUrl(values.getConnected.image),
        };
      }

      if (values.footer) {
        payload.footer = {
          ...pageData.footer,
          ...values.footer,
          logo: {
            light: getSingleImageUrl(values.footer.logo?.light),
            dark: getSingleImageUrl(values.footer.logo?.dark),
          },
        };
      }

      if (activeTab === "Breadcrumb") {
        const updated = [...(pageData.breadcrumb || [])];
        const item = values.breadcrumb?.[0];

        if (item) {
          const data = {
            ...item,
            image: getSingleImageUrl(item.image),
          };

          if (reviewMode === "add") updated.push(data);
          if (reviewMode === "edit" && editingReviewIndex !== null) {
            updated[editingReviewIndex] = {
              ...updated[editingReviewIndex],
              ...data,
            };
          }
        }

        payload.breadcrumb = updated;
      }

      updateHomeData(payload, {
        onSuccess: (res) => {
          message.success("Home page updated successfully");
          setPageData(res.data);
          setIsEditing(false);
          setReviewMode("list");
          setEditingReviewIndex(null);
        },
        onError: () => message.error("Failed to update home page"),
      });
    } catch (err) {
      console.error("❌ Validation failed:", err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setReviewMode("list");
    setEditingReviewIndex(null);
    form.resetFields();
  };

  const handleDelete = (index: number, field: SectionField) => {
    if (!pageData) return;

    let payload: IHomePagePayload;
    if (field === "trackRecord") {
      const current = pageData.trackRecord?.track ?? [];
      const updated = current.filter((_, i) => i !== index);

      payload = {
        ...pageData,
        trackRecord: {
          ...pageData.trackRecord,
          track: updated,
        },
      };
    } else if (field === "breadcrumb") {
      const current = pageData.breadcrumb ?? [];
      const updated = current.filter((_, i) => i !== index);

      payload = {
        ...pageData,
        breadcrumb: updated,
      };
    } else if (field === "globalReach") {
      const current = pageData.globalReach?.locationPin ?? [];
      const updated = current.filter((_, i) => i !== index);

      payload = {
        ...pageData,
        globalReach: {
          ...pageData.globalReach,
          locationPin: updated,
        },
      };
    } else {
      payload = { ...pageData };
    }


    updateHomeData(payload, {
      onSuccess: () => {
        message.success("Data deleted successfully");
        setPageData(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => message.error("Failed to delete"),
    });
  };

  const { data: service, isLoading } = useServiceList({ page: 1, limit: 20 });
  const serviceList: ServiceItem[] = service?.data ?? [];
  
  const { data: industry } = useIndustryList({ page: 1, limit: 20 });
  const IndustryList: IndustryItem[] = industry?.data ?? [];

  const sections = [
    "Breadcrumb",
    "Why Us",
    "What Sets Us Apart",
    "Our Services",
    "Industries We Serve",
    "Global Reach",
    "Track Record",
    "Our Thinking",
    "Get Connected",
    "Footer",
  ];

  return (
    <div>
      <TabsContain
        sections={sections}
        activeTab={activeTab}
        handleChangeTab={handleChangeTab}
      />

      {activeTab === "Breadcrumb" && (
        <HomeBanner
          breadcrumb={pageData?.breadcrumb}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
          mode={reviewMode}
          editingIndex={editingReviewIndex}
          setMode={setReviewMode}
          onDelete={handleDelete}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Why Us" && (
        <WhyUsSection
          whyUsData={pageData?.whyUs}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
        />
      )}

      {activeTab === "What Sets Us Apart" && (
        <WhatSetsUsApart
          whatSetsUsApart={pageData?.whatSetsUsApart}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
        />
      )}

      {activeTab === "Our Services" && (
        <OurServicesSection
          ourServicesData={pageData?.ourServices}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          serviceList={serviceList}
          loading={isPending}
        />
      )}

      {activeTab === "Industries We Serve" && (
        <IndustriesWeServe
          industriesWeServe={pageData?.industriesWeServe}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
          industryList = {IndustryList}
        />
      )}

      {activeTab === "Global Reach" && (
        <GlobalReach
          globalReach={pageData?.globalReach}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
          onDelete={handleDelete}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Track Record" && (
        <TrackRecord
          trackRecord={pageData?.trackRecord}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          onDelete={handleDelete}
          loading={isPending}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Our Thinking" && (
        <OurThinking
          ourThinking={pageData?.ourThinking}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          onDelete={handleDelete}
          loading={isPending}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Get Connected" && (
        <GetConnected
          getConnected={pageData?.getConnected}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
        />
      )}

      {activeTab === "Footer" && (
        <Footer
          footerData={pageData?.footer}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
        />
      )}
    </div>
  );
};

export default HomeTabs;
